"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { doc, onSnapshot } from "firebase/firestore";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { db } from "../../../../firebase";
import type { RootState } from "../../../store";
import {
  fetchMyMerchant,
  type ApiError,
  type Merchant,
  type MyState,
} from "../../../services/merchantApi";
import { ErrorCard, InlineNotice, primaryBtn } from "../ui";

// ────────────────────────────────────────────────────────────────────────────
// WebhooksGate — the one access gate in front of all three webhooks routes
// (/developers/webhooks, …/endpoints/:id, …/deliveries/:id).
//
//   wallet still null        → spinner. NO API call and nothing latched: the
//                              backend would resolve the caller from the
//                              email alone and answer 403 for a real merchant
//   business flag unknown    → spinner
//   not a business account   → amber "Business accounts only" card
//   merchant lookup running  → spinner
//   merchant lookup failed   → ErrorCard + Retry
//   none / rejected / pending→ amber "Get API access first" + link
//   approved                 → children({ wallet, email, merchant })
//
// The page's <Toaster> is mounted HERE, once, above every branch — there is
// no global Toaster in this app, and an email deep link can land on any of
// these states.
// ────────────────────────────────────────────────────────────────────────────

/** What an approved page receives from the gate. `wallet` is never null here. */
export interface WebhooksGateContext {
  wallet: string;
  email: string | null;
  merchant: Merchant;
}

// `forWallet` pins a result to the identity it was fetched for, so a wallet
// change can never render the previous account's merchant, not even for
// the one frame before the refetch effect runs.
type MerchantLookup =
  | { phase: "loading" }
  | { phase: "error"; forWallet: string; message: string; requestId: string | null }
  | { phase: "ready"; forWallet: string; state: MyState };

/**
 * Render-prop gate: business account → approved merchant.
 * @param children called only in the approved state with `{ wallet, email, merchant }`;
 *                 the page renders its own container inside
 * @example
 * <WebhooksGate>{({ wallet, email, merchant }) => <Page … />}</WebhooksGate>
 */
export function WebhooksGate({
  children,
}: {
  children: (ctx: WebhooksGateContext) => ReactNode;
}) {
  const user = useSelector((s: RootState) => s.user);
  const wallet: string | null = user?.wallet || user?.smartWallet || null;
  const email: string | null = user?.email || null;

  // `isBusiness` comes from a live Firestore snapshot, not the Redux mirror:
  // on a hard refresh (exactly what an email deep link is) Redux hydrates
  // after first render and would flash the "Business accounts only" card at
  // a real business user. Same pattern as ApiSettingsPage and Layout.
  const [businessStatus, setBusinessStatus] = useState<
    "unknown" | "business" | "non-business"
  >("unknown");
  useEffect(() => {
    if (!wallet) {
      setBusinessStatus("unknown");
      return;
    }
    const unsub = onSnapshot(
      doc(db, "users", wallet),
      (snap) => {
        if (snap.exists() && snap.data().isBusiness) {
          setBusinessStatus("business");
        } else {
          setBusinessStatus("non-business");
        }
      },
      () => {
        // Firestore read failed — fall back to the Redux mirror rather
        // than crash; the gate card is harmless if we guess wrong.
        setBusinessStatus(user?.isBusiness ? "business" : "non-business");
      },
    );
    return () => unsub();
  }, [wallet, user?.isBusiness]);
  const isBusiness = businessStatus === "business";

  const [lookup, setLookup] = useState<MerchantLookup>({ phase: "loading" });
  // Drops answers that arrive after the identity changed or a retry started.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    // Never ask without a wallet, and never for a non-business account.
    if (!wallet || !isBusiness) return;
    const seq = ++requestSeq.current;
    setLookup({ phase: "loading" });
    try {
      const state = await fetchMyMerchant(wallet, email);
      if (seq !== requestSeq.current) return;
      setLookup({ phase: "ready", forWallet: wallet, state });
    } catch (e) {
      if (seq !== requestSeq.current) return;
      const err = e as ApiError | undefined;
      setLookup({
        phase: "error",
        forWallet: wallet,
        message: err?.message || "Failed to load.",
        requestId: err?.requestId || null,
      });
    }
  }, [wallet, email, isBusiness]);

  useEffect(() => {
    load();
    return () => {
      // Invalidate whatever is still in flight for the previous identity.
      requestSeq.current += 1;
    };
  }, [load]);

  let body: ReactNode;
  if (!wallet || businessStatus === "unknown") {
    body = <GateSpinner label="Loading your account…" />;
  } else if (!isBusiness) {
    body = (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-100 font-semibold">Business accounts only</p>
              <p className="text-amber-200/80 text-sm mt-1 leading-relaxed">
                Webhooks are part of the Checkout API, which is available to business accounts.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (lookup.phase === "loading" || lookup.forWallet !== wallet) {
    body = <GateSpinner label="Loading webhooks…" />;
  } else if (lookup.phase === "error") {
    body = (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <ErrorCard
          title="Couldn't load webhooks"
          message={lookup.message}
          requestId={lookup.requestId}
          onRetry={load}
        />
      </div>
    );
  } else if (lookup.state.state !== "approved") {
    body = (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <InlineNotice
          tone="warn"
          icon={Lock}
          title="Get API access first"
          action={
            <Link to="/developers/api-settings" className={primaryBtn}>
              Go to API Settings <ArrowRight className="w-4 h-4" />
            </Link>
          }
        >
          Webhooks become available once your Checkout API access is approved.
        </InlineNotice>
      </div>
    );
  } else {
    body = children({ wallet, email, merchant: lookup.state.merchant });
  }

  return (
    <>
      <Toaster position="top-center" />
      {body}
    </>
  );
}

function GateSpinner({ label }: { label: string }) {
  return (
    <div
      role="status"
      className="max-w-3xl mx-auto py-24 px-4 flex flex-col items-center justify-center gap-3"
    >
      <Loader2 className="w-6 h-6 text-[#61CE70] animate-spin" />
      <p className="text-gray-500 text-sm">{label}</p>
    </div>
  );
}
